import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { 
  User, 
  BookOpen, 
  GraduationCap, 
  Save,
  ArrowLeft,
  Loader2
} from 'lucide-react';

const StudentProfile = () => {
  // const { user } = useAuth(); // Removed unused variable
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [existingData, setExistingData] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm();

  // Watch form values for real-time validation
  const watchedValues = watch();

  const fetchExistingProfile = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/students/profile');
        console.log(response.data);
        setExistingData(response.data);
        
        // Pre-fill form with existing data
        if (response.data) {
          setValue('personalInfo.firstName', response.data.personalInfo?.firstName || '');
          setValue('personalInfo.lastName', response.data.personalInfo?.lastName || '');
          // Convert ISO date to yyyy-MM-dd format for HTML date input
          const dateOfBirth = response.data.personalInfo?.dateOfBirth;
          if (dateOfBirth) {
            const date = new Date(dateOfBirth);
            const formattedDate = date.toISOString().split('T')[0]; // Convert to yyyy-MM-dd
            setValue('personalInfo.dateOfBirth', formattedDate);
          } else {
            setValue('personalInfo.dateOfBirth', '');
          }
          setValue('personalInfo.gender', response.data.personalInfo?.gender || '');
          setValue('personalInfo.parentName', response.data.personalInfo?.parentName || '');
          setValue('personalInfo.parentPhone', response.data.personalInfo?.parentPhone || '');
          setValue('personalInfo.parentEmail', response.data.personalInfo?.parentEmail || '');
          
          setValue('highSchoolMarks.math', response.data.highSchoolMarks?.math || '');
          setValue('highSchoolMarks.science', response.data.highSchoolMarks?.science || '');
          setValue('highSchoolMarks.english', response.data.highSchoolMarks?.english || '');
          setValue('highSchoolMarks.hindi', response.data.highSchoolMarks?.hindi || '');
          
          setValue('twelfthMarks.physics', response.data.twelfthMarks?.physics || '');
          setValue('twelfthMarks.chemistry', response.data.twelfthMarks?.chemistry || '');
          setValue('twelfthMarks.mathematics', response.data.twelfthMarks?.mathematics || '');
          
          if (response.data.branchChoices && response.data.branchChoices.length > 0) {
            setValue('branchChoices.0.branch', response.data.branchChoices[0]?.branch || '');
            setValue('branchChoices.0.priority', response.data.branchChoices[0]?.priority || 1);
            if (response.data.branchChoices.length > 1) {
              setValue('branchChoices.1.branch', response.data.branchChoices[1]?.branch || '');
              setValue('branchChoices.1.priority', response.data.branchChoices[1]?.priority || 2);
            }
          }
          setLoading(false);
        }
      } catch (error) {
        // If 404, no profile exists yet - that's fine
        if (error.response?.status !== 404) {
          console.error('Error fetching profile:', error);
        }
        setLoading(false);
      }
    };

useEffect(() => {
    fetchExistingProfile();
  }, [setValue]);


  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Validate marks are within 0-100 range
      const marks = [
        data.highSchoolMarks.math,
        data.highSchoolMarks.science,
        data.highSchoolMarks.english,
        data.highSchoolMarks.hindi,
        data.twelfthMarks.physics,
        data.twelfthMarks.chemistry,
        data.twelfthMarks.mathematics
      ];

      for (let mark of marks) {
        if (mark < 0 || mark > 100) {
          toast.error('All marks must be between 0 and 100');
          setLoading(false);
          return;
        }
      }

      // Validate branch choices
      const branchChoices = [];
      if (data.branchChoices[0]?.branch) {
        branchChoices.push({
          branch: data.branchChoices[0].branch,
          priority: 1
        });
      }
      if (data.branchChoices[1]?.branch && data.branchChoices[1].branch !== data.branchChoices[0]?.branch) {
        branchChoices.push({
          branch: data.branchChoices[1].branch,
          priority: 2
        });
      }

      if (branchChoices.length === 0) {
        toast.error('Please select at least one branch preference');
        setLoading(false);
        return;
      }

      const profileData = {
        personalInfo: {
          firstName: data.personalInfo.firstName,
          lastName: data.personalInfo.lastName,
          dateOfBirth: data.personalInfo.dateOfBirth,
          gender: data.personalInfo.gender,
          parentName: data.personalInfo.parentName,
          parentPhone: data.personalInfo.parentPhone,
          parentEmail: data.personalInfo.parentEmail
        },
        highSchoolMarks: {
          math: parseFloat(data.highSchoolMarks.math),
          science: parseFloat(data.highSchoolMarks.science),
          english: parseFloat(data.highSchoolMarks.english),
          hindi: parseFloat(data.highSchoolMarks.hindi)
        },
        twelfthMarks: {
          physics: parseFloat(data.twelfthMarks.physics),
          chemistry: parseFloat(data.twelfthMarks.chemistry),
          mathematics: parseFloat(data.twelfthMarks.mathematics)
        },
        branchChoices
      };

      await axios.post('/api/students/profile', profileData);
      
      toast.success(existingData ? 'Profile updated successfully!' : 'Profile created successfully!');
      navigate('/student/');
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to save profile';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = (marks) => {
    return Object.values(marks).reduce((sum, mark) => sum + (parseFloat(mark) || 0), 0);
  };

  const highSchoolTotal = calculateTotal(watchedValues.highSchoolMarks || {});
  const twelfthTotal = calculateTotal(watchedValues.twelfthMarks || {});

  if(loading){
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/student')}
          className="flex items-center text-primary-600 hover:text-primary-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Student Profile</h1>
        <p className="text-gray-600 mt-2">
          {existingData ? 'Update your profile information' : 'Complete your profile to start your admission process'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Personal Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-6">
            <User className="h-6 w-6 text-primary-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name *
              </label>
              <input
                type="text"
                {...register('personalInfo.firstName', { required: 'First name is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Enter first name"
              />
              {errors.personalInfo?.firstName && (
                <p className="text-red-500 text-sm mt-1">{errors.personalInfo.firstName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name *
              </label>
              <input
                type="text"
                {...register('personalInfo.lastName', { required: 'Last name is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Enter last name"
              />
              {errors.personalInfo?.lastName && (
                <p className="text-red-500 text-sm mt-1">{errors.personalInfo.lastName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth *
              </label>
              <input
                type="date"
                {...register('personalInfo.dateOfBirth', { required: 'Date of birth is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {errors.personalInfo?.dateOfBirth && (
                <p className="text-red-500 text-sm mt-1">{errors.personalInfo.dateOfBirth.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gender *
              </label>
              <select
                {...register('personalInfo.gender', { required: 'Gender is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {errors.personalInfo?.gender && (
                <p className="text-red-500 text-sm mt-1">{errors.personalInfo.gender.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parent/Guardian Name *
              </label>
              <input
                type="text"
                {...register('personalInfo.parentName', { required: 'Parent name is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Enter parent/guardian name"
              />
              {errors.personalInfo?.parentName && (
                <p className="text-red-500 text-sm mt-1">{errors.personalInfo.parentName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parent Phone *
              </label>
              <input
                type="tel"
                {...register('personalInfo.parentPhone', { required: 'Parent phone is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Enter parent phone number"
              />
              {errors.personalInfo?.parentPhone && (
                <p className="text-red-500 text-sm mt-1">{errors.personalInfo.parentPhone.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parent Email *
              </label>
              <input
                type="email"
                {...register('personalInfo.parentEmail', { 
                  required: 'Parent email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Enter parent email address"
              />
              {errors.personalInfo?.parentEmail && (
                <p className="text-red-500 text-sm mt-1">{errors.personalInfo.parentEmail.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* High School Marks */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-6">
            <BookOpen className="h-6 w-6 text-primary-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">High School Marks (Class 10)</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mathematics *
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                {...register('highSchoolMarks.math', { 
                  required: 'Mathematics marks are required',
                  min: { value: 0, message: 'Marks must be at least 0' },
                  max: { value: 100, message: 'Marks cannot exceed 100' }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0-100"
              />
              {errors.highSchoolMarks?.math && (
                <p className="text-red-500 text-sm mt-1">{errors.highSchoolMarks.math.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Science *
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                {...register('highSchoolMarks.science', { 
                  required: 'Science marks are required',
                  min: { value: 0, message: 'Marks must be at least 0' },
                  max: { value: 100, message: 'Marks cannot exceed 100' }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0-100"
              />
              {errors.highSchoolMarks?.science && (
                <p className="text-red-500 text-sm mt-1">{errors.highSchoolMarks.science.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                English *
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                {...register('highSchoolMarks.english', { 
                  required: 'English marks are required',
                  min: { value: 0, message: 'Marks must be at least 0' },
                  max: { value: 100, message: 'Marks cannot exceed 100' }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0-100"
              />
              {errors.highSchoolMarks?.english && (
                <p className="text-red-500 text-sm mt-1">{errors.highSchoolMarks.english.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hindi *
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                {...register('highSchoolMarks.hindi', { 
                  required: 'Hindi marks are required',
                  min: { value: 0, message: 'Marks must be at least 0' },
                  max: { value: 100, message: 'Marks cannot exceed 100' }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0-100"
              />
              {errors.highSchoolMarks?.hindi && (
                <p className="text-red-500 text-sm mt-1">{errors.highSchoolMarks.hindi.message}</p>
              )}
            </div>
          </div>

          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <p className="text-sm font-medium text-gray-700">
              High School Total: <span className="text-primary-600 font-bold">{highSchoolTotal}/400</span>
            </p>
          </div>
        </div>

        {/* 12th Standard Marks */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-6">
            <BookOpen className="h-6 w-6 text-primary-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">12th Standard Marks (Class 12)</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Physics *
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                {...register('twelfthMarks.physics', { 
                  required: 'Physics marks are required',
                  min: { value: 0, message: 'Marks must be at least 0' },
                  max: { value: 100, message: 'Marks cannot exceed 100' }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0-100"
              />
              {errors.twelfthMarks?.physics && (
                <p className="text-red-500 text-sm mt-1">{errors.twelfthMarks.physics.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chemistry *
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                {...register('twelfthMarks.chemistry', { 
                  required: 'Chemistry marks are required',
                  min: { value: 0, message: 'Marks must be at least 0' },
                  max: { value: 100, message: 'Marks cannot exceed 100' }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0-100"
              />
              {errors.twelfthMarks?.chemistry && (
                <p className="text-red-500 text-sm mt-1">{errors.twelfthMarks.chemistry.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mathematics *
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                {...register('twelfthMarks.mathematics', { 
                  required: 'Mathematics marks are required',
                  min: { value: 0, message: 'Marks must be at least 0' },
                  max: { value: 100, message: 'Marks cannot exceed 100' }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0-100"
              />
              {errors.twelfthMarks?.mathematics && (
                <p className="text-red-500 text-sm mt-1">{errors.twelfthMarks.mathematics.message}</p>
              )}
            </div>
          </div>

          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <p className="text-sm font-medium text-gray-700">
              12th Standard Total: <span className="text-primary-600 font-bold">{twelfthTotal}/300</span>
            </p>
          </div>
        </div>

        {/* Branch Preferences */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-6">
            <GraduationCap className="h-6 w-6 text-primary-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Branch Preferences</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Preference *
              </label>
              <select
                {...register('branchChoices.0.branch', { required: 'First preference is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select branch</option>
                <option value="Computer Science">Computer Science</option>
                <option value="Mechanical Engineering">Mechanical Engineering</option>
                <option value="Electrical Engineering">Electrical Engineering</option>
                <option value="Civil Engineering">Civil Engineering</option>
                <option value="Chemical Engineering">Chemical Engineering</option>
              </select>
              {errors.branchChoices?.[0]?.branch && (
                <p className="text-red-500 text-sm mt-1">{errors.branchChoices[0].branch.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Second Preference (Optional)
              </label>
              <select
                {...register('branchChoices.1.branch')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select branch</option>
                <option value="Computer Science">Computer Science</option>
                <option value="Mechanical Engineering">Mechanical Engineering</option>
                <option value="Electrical Engineering">Electrical Engineering</option>
                <option value="Civil Engineering">Civil Engineering</option>
                <option value="Chemical Engineering">Chemical Engineering</option>
              </select>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/student')}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {existingData ? 'Update Profile' : 'Save Profile'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StudentProfile;
